"use client"

import { Button } from "@/src/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/src/components/ui/dialog"
import { TooltipProvider } from "@/src/components/ui/tooltip"
import { DateRangeType } from "@/src/constants/date-range-items"
import useCustomRouter from "@/src/hooks/use-custom-router"
import { useInitialDateRange } from "@/src/utils/date-handler"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createContext, useCallback, useContext, useState } from "react"
import { useDescope } from "@descope/nextjs-sdk/client"

export type AppProvidersProps = Readonly<{
  children: React.ReactNode
}>

const LogoutContext = createContext(
  {} as {
    logout: () => void
  },
)

const ConnectedContext = createContext(
  {} as {
    disconnectAlert: (message?: string) => void
  },
)

const PresetContext = createContext(
  {} as {
    dateRangePreset: DateRangeType
    setDateRangePreset: (val: DateRangeType) => void
  },
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

export function AppProvider(props: AppProvidersProps) {
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [dateRangePreset, setDateRangePreset] = useState<DateRangeType>(useInitialDateRange())
  const [disconnectText, setDisconnectText] = useState(
    "The chosen cluster is disconnected, please retry after reconnecting.",
  )
  const { navigate } = useCustomRouter()
  const descope = useDescope()

  const logout = () => {
    // setShowLogoutDialog(true)
    handleReload() // don't ask the user, reload directly
  }

  const disconnectAlert = (message?: string) => {
    setShowDisconnectDialog(true)
    setDisconnectText(message ?? "The chosen cluster is disconnected, please retry after reconnecting.")
  }

  const handleReload = useCallback(() => {
    if (window) {
      setShowLogoutDialog(false)
      window.location.reload()
    }
  }, [])

  const handlelogout = useCallback(() => {
    descope.logout().then(() => {
      setShowLogoutDialog(false)
      navigate("/sign-in")
    })
  }, [])

  return (
    <TooltipProvider>
      {/* Logout Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={(open) => setShowLogoutDialog(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Session Expired</DialogTitle>
            <DialogDescription>
              Your session has expired. Please log in again or reload the page.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button onClick={handlelogout}>Login again</Button>
            <Button onClick={handleReload}>Reload</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Disconnected Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={(open) => setShowDisconnectDialog(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cluster Disconnected</DialogTitle>
            <DialogDescription>{disconnectText}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button onClick={() => setShowDisconnectDialog(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
      <QueryClientProvider client={queryClient}>
        <LogoutContext.Provider value={{ logout }}>
          <PresetContext.Provider value={{ dateRangePreset, setDateRangePreset }}>
            <ConnectedContext.Provider value={{ disconnectAlert }}>
              {props.children}
            </ConnectedContext.Provider>
          </PresetContext.Provider>
        </LogoutContext.Provider>
      </QueryClientProvider>
    </TooltipProvider>
  )
}

export const useLogout = () => useContext(LogoutContext)
export const useDatePreset = () => useContext(PresetContext)
export const useDisconnect = () => useContext(ConnectedContext)
